import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin';
import { AdminLayout } from '../../components/AdminLayout';

export function AdminCustomersPage() {
  const customersQuery = useQuery({
    queryKey: ['admin', 'customers'],
    queryFn: adminApi.listCustomers,
  });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-wc-black">Customers</h1>
          <p className="text-sm text-wc-muted-strong">
            Each customer combines email domains, an OIDC identity provider, and
            a GitLab project that backs their portal content.
          </p>
        </div>
        <Link to="/admin/customers/new" className="btn-primary">
          New customer
        </Link>
      </div>

      <div className="card mt-8 overflow-hidden">
        {customersQuery.isLoading ? (
          <p className="p-8 text-sm text-wc-muted">Loading…</p>
        ) : customersQuery.isError ? (
          <p className="p-8 text-sm text-wc-red">Failed to load customers.</p>
        ) : customersQuery.data?.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-wc-muted-strong">
              No customers yet. Click <strong>New customer</strong> to onboard your first one.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-wc-muted/15 text-sm">
            <thead className="bg-wc-gray/60 text-xs uppercase tracking-wider text-wc-muted-strong">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">Customer</th>
                <th className="px-6 py-3 text-left font-semibold">Slug</th>
                <th className="px-6 py-3 text-left font-semibold">Domains</th>
                <th className="px-6 py-3 text-left font-semibold">OIDC</th>
                <th className="px-6 py-3 text-left font-semibold">GitLab</th>
                <th className="px-6 py-3 text-left font-semibold">Status</th>
                <th className="px-6 py-3 text-right font-semibold">Audit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-wc-muted/15">
              {customersQuery.data?.map((c) => (
                <tr key={c.id} className="hover:bg-wc-gray/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/customers/${c.id}`}
                        className="font-medium text-wc-blue hover:underline"
                      >
                        {c.displayName}
                      </Link>
                      {c.isGlobal && (
                        <span
                          className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800"
                          title="Users of this customer can switch into any other active customer."
                        >
                          Global
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-wc-muted-strong">{c.slug}</td>
                  <td className="px-6 py-4 text-wc-muted-strong">{c.domainCount}</td>
                  <td className="px-6 py-4">
                    {c.authMethod === 'email_code' ? (
                      <StatusPill ok okLabel="Email code" missingLabel="" />
                    ) : (
                      <StatusPill ok={c.oidcConfigured} okLabel="OIDC" missingLabel="Missing" />
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <StatusPill
                      ok={c.gitlabConfigured}
                      okLabel={c.gitlabMode === 'pages' ? 'Pages' : 'Repo'}
                      missingLabel="Missing"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <StatusPill
                      ok={c.status === 'active'}
                      okLabel="Active"
                      missingLabel={c.status === 'suspended' ? 'Suspended' : c.status}
                    />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/admin/customers/${c.id}/audit`}
                      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-wc-blue hover:underline"
                      title={`View audit dashboard for ${c.displayName}`}
                    >
                      View
                      <span aria-hidden>→</span>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

function StatusPill({
  ok,
  okLabel,
  missingLabel,
}: {
  ok: boolean;
  okLabel: string;
  missingLabel: string;
}) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        ok ? 'bg-wc-blue/10 text-wc-blue' : 'bg-wc-muted/15 text-wc-muted-strong'
      }`}
    >
      {ok ? okLabel : missingLabel}
    </span>
  );
}
